import { Suspense } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import { RouteChange } from '@/components/layout/RouteChange'
import { ChunkErrorBoundary } from '@/components/layout/ChunkErrorBoundary'
import { MaintenanceGate } from '@/components/layout/MaintenanceGate'
import HomePage from '@/pages/Home'
import { NotFoundPage } from '@/pages/NotFound'
import AnnouncementsPage from '@/pages/Announcements'
import MaintenancePage from '@/pages/Maintenance'
import {
  routeComponent,
  loadBuilderPage,
  loadToolsIndexPage,
  loadAtsCheckerPage,
  loadJobMatcherPage,
  loadBulletImproverPage,
  loadAboutPage,
  loadPrivacyPage,
  loadMethodologyPage,
  loadEvidencePage,
  loadGuidesIndex,
  loadLandingPageView,
  loadContactPage,
  loadFaqPage,
  loadHowItWorksPage,
  loadTermsPage,
  loadDisclaimerPage,
  loadAuthorPage,
} from '@/routeChunks'

/**
 * Every route below is code-split via routeComponent (see routeChunks.tsx),
 * not plain React.lazy — see that file's comment for why a bare lazy()
 * cannot hydrate its first render cleanly against prerendered HTML. The
 * homepage is the SEO entry point and highest-traffic page, so it must not
 * pay JS-parse cost for pages a visitor hasn't navigated to — `App.tsx`
 * used to statically import all of these, which put the entire
 * Tools/Landing/Methodology/etc. page tree (and, transitively, their icons
 * and data) into the homepage's initial bundle.
 *
 * scripts/prerender.ts does NOT render through this file — see AppServer.tsx
 * for why (renderToString can't await a lazy import) and for the parallel,
 * eager route tree it renders through instead. Every route added or removed
 * here must be mirrored there.
 */
const BuilderPage = routeComponent(loadBuilderPage)
const ToolsIndexPage = routeComponent(loadToolsIndexPage)
const AtsCheckerPage = routeComponent(loadAtsCheckerPage)
const JobMatcherPage = routeComponent(loadJobMatcherPage)
const BulletImproverPage = routeComponent(loadBulletImproverPage)
const AboutPage = routeComponent(loadAboutPage)
const PrivacyPage = routeComponent(loadPrivacyPage)
const MethodologyPage = routeComponent(loadMethodologyPage)
const EvidencePage = routeComponent(loadEvidencePage)
const GuidesIndex = routeComponent(loadGuidesIndex)
const LandingPageView = routeComponent(loadLandingPageView)
const ContactPage = routeComponent(loadContactPage)
const FaqPage = routeComponent(loadFaqPage)
const HowItWorksPage = routeComponent(loadHowItWorksPage)
const TermsPage = routeComponent(loadTermsPage)
const DisclaimerPage = routeComponent(loadDisclaimerPage)
const AuthorPage = routeComponent(loadAuthorPage)

const PageFallback = () => (
  <div className="flex min-h-dvh items-center justify-center bg-base-200">
    <p className="text-sm opacity-70">Loading…</p>
  </div>
)

export const App = () => (
  <>
    {/* Scroll and focus reset on navigation. Renders nothing. */}
    <RouteChange />
    {/*
      Redirects to /maintenance while maintenance.enabled is on, and back once
      it isn't — see MaintenanceGate and docs/announcements-maintenance.md.
    */}
    <MaintenanceGate>
      {/*
        One boundary for the whole route tree, not one per <Route>: React
        Router only ever renders a single matched route at a time, so a
        shared Suspense/error boundary behaves identically to per-route ones
        here, with far less repetition. Home/Announcements/Maintenance/
        NotFound are not lazy, so they render straight through — Suspense
        with no pending descendant is a no-op.
      */}
      <ChunkErrorBoundary>
        <Suspense fallback={<PageFallback />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/announcements" element={<AnnouncementsPage />} />
            <Route path="/maintenance" element={<MaintenancePage />} />
            <Route path="/resume-builder" element={<BuilderPage />} />
            {/*
              The host-level 301 in public/.htaccess handles
              every real HTTP request to the old URL. This only catches a client-side
              navigation to it — an internal <Link> that still points at /builder, or
              a stale entry in the service worker cache — which a server redirect
              never sees because React Router intercepts it before a request is made.
            */}
            <Route path="/builder" element={<Navigate to="/resume-builder" replace />} />

            <Route path="/tools" element={<ToolsIndexPage />} />
            <Route path="/ats-resume-checker" element={<AtsCheckerPage />} />
            <Route path="/job-description-matcher" element={<JobMatcherPage />} />
            <Route path="/resume-bullet-improver" element={<BulletImproverPage />} />

            <Route path="/guides" element={<GuidesIndex />} />
            <Route path="/guides/:slug" element={<LandingPageView prefix="guides" />} />

            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/methodology" element={<MethodologyPage />} />
            <Route path="/evidence" element={<EvidencePage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/faq" element={<FaqPage />} />
            <Route path="/how-it-works" element={<HowItWorksPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/disclaimer" element={<DisclaimerPage />} />
            <Route path="/author/atsresumekit-team" element={<AuthorPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </ChunkErrorBoundary>
    </MaintenanceGate>
  </>
)

export default App
